import { useEffect, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { Link } from "react-router-dom";
import { api, ApiError, SaaSOverview, OverviewSnapshot, ReplayJob, AggregateDomain, AggregateDomainReconciliationRun } from "../../lib/api.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

type Stat = { label: string; value: string | number; hint?: string };

function relativeTime(iso?: string): string {
  if (!iso) return "never";
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return "—";
  const deltaMs = Date.now() - then;
  if (deltaMs < 0) return "soon";
  const mins = Math.floor(deltaMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function AdminOverview() {
  const [saas, setSaas] = useState<SaaSOverview | null>(null);
  const [overview, setOverview] = useState<OverviewSnapshot | null>(null);
  const [jobs, setJobs] = useState<ReplayJob[]>([]);
  const [providers, setProviders] = useState<Awaited<ReturnType<typeof api.paymentProviders>>["providers"]>([]);
  const [packages, setPackages] = useState<number>(0);
  const [forwarding, setForwarding] = useState<Awaited<ReturnType<typeof api.platformForwarding>> | null>(null);
  const [connections, setConnections] = useState<Awaited<ReturnType<typeof api.platformConnections>> | null>(null);
  const [security, setSecurity] = useState<Awaited<ReturnType<typeof api.securityCenter>> | null>(null);
  const [aggDomains, setAggDomains] = useState<AggregateDomain[]>([]);
  const [aggReconciliation, setAggReconciliation] = useState<{ latest: AggregateDomainReconciliationRun | null; varianceCount: number } | null>(null);
  const [lastRollupRun, setLastRollupRun] = useState<{ status: string; mode: string; startedAt: string; completedAt?: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.saasOverview(),
      api.overview().catch(() => null),
      api.jobs().catch(() => ({ jobs: [] })),
      api.paymentProviders().catch(() => ({ providers: [] })),
      api.subscriptionPackages().catch(() => ({ packages: [] })),
      api.platformForwarding().catch(() => null),
      api.platformConnections().catch(() => null),
      api.securityCenter().catch(() => null),
      api.aggregateDomains().catch(() => null),
      api.aggregateReconciliation().catch(() => null),
      api.request<{ runs: Array<{ status: string; mode: string; startedAt: string; completedAt?: string }> }>("/api/submitted-aggregate/rollups").catch(() => null)
    ])
      .then(([s, o, j, pv, pk, fw, cn, sc, ad, ar, rr]) => {
        if (cancelled) return;
        setSaas(s);
        setOverview(o);
        setJobs(j.jobs ?? []);
        setProviders(pv.providers ?? []);
        setPackages(pk.packages?.length ?? 0);
        setForwarding(fw);
        setConnections(cn);
        setSecurity(sc);
        setAggDomains(ad?.domains ?? []);
        if (ar) {
          setAggReconciliation({
            latest: ar.latest,
            varianceCount: (ar.comparisons ?? []).filter((c) => c.variance !== 0).length
          });
        }
        const runs = (rr?.runs ?? []).slice().sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt));
        setLastRollupRun(runs[0] ?? null);
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const tProviders = useTableState(providers as unknown as Array<Record<string, unknown>>, {
    searchFields: ["name", "displayName"],
    initialPageSize: 25
  });

  const activeProviders = providers.filter((p) => p.enabled).length;
  const configuredProviders = providers.filter((p) => p.configured).length;

  const tenancyStats: Stat[] = saas ? [
    { label: "Workspaces", value: saas.workspaceCount, hint: `${saas.activeWorkspaceCount} active` },
    { label: "Subscription packages", value: packages },
    { label: "Active payment gateways", value: activeProviders, hint: `${configuredProviders} configured` },
    { label: "Public portals", value: saas.activePortalCount, hint: "active" }
  ] : [];

  const pipelineStats: Stat[] = overview ? [
    { label: "Tallies (all tenants)", value: Number(overview.metadataTallyCount ?? 0) },
    { label: "Indicators", value: Number(overview.metadataIndicatorCount ?? 0) },
    { label: "Jobs queued", value: Number(overview.replayJobsQueued ?? 0) },
    { label: "Jobs running", value: Number(overview.replayJobsRunning ?? 0) },
    { label: "Jobs failed", value: Number(overview.replayJobsFailed ?? 0) }
  ] : [];

  const failed24h = jobs.filter((j) => {
    if (j.status !== "failed" && j.status !== "dead-lettered") return false;
    const t = Date.parse(j.updatedAt ?? j.createdAt ?? "");
    return Number.isFinite(t) && Date.now() - t < 24 * 3600 * 1000;
  }).length;

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Platform overview</h1>
          <p>Cross-tenant state of TallyStream SaaS: tenancy, monetisation, and pipeline health.</p>
        </div>
      </header>

      {err && <div className="alert alert-error">{err}</div>}

      {loading ? <SkeletonTable rows={5} columns={4} /> : (
        <>
          <section className="section">
            <div className="section-head"><h2>Tenancy</h2></div>
            <div className="stat-grid">
              {tenancyStats.map((s) => (
                <div key={s.label} className="stat">
                  <div className="stat-label">{s.label}</div>
                  <div className="stat-value">{s.value}</div>
                  {s.hint && <div className="stat-hint">{s.hint}</div>}
                </div>
              ))}
            </div>
          </section>

          <section className="section">
            <div className="section-head">
              <h2>Payment gateways</h2>
              <Link to="/admin/payment-gateways" className="linkbtn">Manage</Link>
            </div>
            {providers.length === 0 ? (
              <div className="empty">No payment providers configured yet.</div>
            ) : (
              <div>
                <TableToolbar
                  search={tProviders.search}
                  setSearch={tProviders.setSearch}
                  page={tProviders.page}
                  setPage={tProviders.setPage}
                  pageSize={tProviders.pageSize}
                  setPageSize={tProviders.setPageSize}
                  totalPages={tProviders.totalPages}
                  totalRows={tProviders.totalRows}
                  hideExport
                  searchPlaceholder="Search provider…"
                />
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr><th>Provider</th><th>Status</th><th>Missing fields</th></tr>
                  </thead>
                  <tbody>
                    {(tProviders.paged as unknown as typeof providers).map((p) => (
                      <tr key={p.name}>
                        <td><strong>{p.displayName}</strong></td>
                        <td>
                          {p.enabled
                            ? <span className="pill pill-ok">Enabled</span>
                            : p.configured
                            ? <span className="pill pill-warn">Disabled</span>
                            : <span className="pill pill-muted">Not configured</span>}
                        </td>
                        <td className="soft">{p.missingRequired.length === 0 ? "—" : p.missingRequired.join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </div>
            )}
          </section>

          {(forwarding || connections) && (
            <section className="section">
              <div className="section-head"><h2>Integrations health</h2></div>
              <div className="stat-grid">
                {forwarding && (
                  <>
                    <div className="stat">
                      <div className="stat-label">Forwarding destinations</div>
                      <div className="stat-value">{forwarding.summary.destinationCount}</div>
                      <div className="stat-hint">{forwarding.summary.activeDestinationCount} active · {forwarding.summary.pausedDestinationCount} paused</div>
                    </div>
                    <div className="stat">
                      <div className="stat-label">Failed forwarding runs</div>
                      <div className="stat-value" style={{ color: forwarding.summary.recentFailedRuns > 0 ? "#b91c1c" : undefined }}>
                        {forwarding.summary.recentFailedRuns}
                      </div>
                      <div className="stat-hint"><Link to="/admin/forwarding">View oversight</Link></div>
                    </div>
                  </>
                )}
                {connections && (
                  <>
                    <div className="stat">
                      <div className="stat-label">DHIS2 connections</div>
                      <div className="stat-value">{connections.summary.total}</div>
                      <div className="stat-hint">{connections.summary.healthy} healthy</div>
                    </div>
                    <div className="stat">
                      <div className="stat-label">Missing secrets</div>
                      <div className="stat-value" style={{ color: connections.summary.missingSecret > 0 ? "#b91c1c" : undefined }}>
                        {connections.summary.missingSecret}
                      </div>
                      <div className="stat-hint"><Link to="/admin/connections">View connections</Link></div>
                    </div>
                  </>
                )}
              </div>
            </section>
          )}

          {security && (security.metrics.authFailures > 0 || security.metrics.rateLimited > 0 || security.recentEvents.length > 0) && (
            <section className="section">
              <div className="section-head">
                <h2>Security alerts</h2>
                <Link to="/admin/security-center" className="linkbtn">Security center</Link>
              </div>
              <div className="stat-grid">
                <div className="stat">
                  <div className="stat-label">Auth failures</div>
                  <div className="stat-value" style={{ color: security.metrics.authFailures > 0 ? "#b91c1c" : undefined }}>
                    {security.metrics.authFailures}
                  </div>
                </div>
                <div className="stat">
                  <div className="stat-label">Rate-limited requests</div>
                  <div className="stat-value">{security.metrics.rateLimited}</div>
                </div>
                <div className="stat">
                  <div className="stat-label">Recent privileged events</div>
                  <div className="stat-value">{security.recentEvents.length}</div>
                </div>
              </div>
            </section>
          )}

          {aggDomains.length > 0 && (
            <section className="section">
              <div className="section-head">
                <h2>Aggregate engine</h2>
                <Link to="/admin/analytics" className="linkbtn">Analytics explorer</Link>
              </div>
              <div className="stat-grid">
                <div className="stat">
                  <div className="stat-label">Domains</div>
                  <div className="stat-value">{aggDomains.filter((d) => d.active && !d.archived).length}</div>
                  <div className="stat-hint">{aggDomains.length} total · {aggDomains.filter((d) => d.archived).length} archived</div>
                </div>
                <div className="stat">
                  <div className="stat-label">Last rollup run</div>
                  <div className="stat-value">{relativeTime(lastRollupRun?.completedAt ?? lastRollupRun?.startedAt)}</div>
                  <div className="stat-hint">{lastRollupRun ? `${lastRollupRun.mode} · ${lastRollupRun.status}` : "no runs yet"}</div>
                </div>
                <div className="stat">
                  <div className="stat-label">Last reconciliation</div>
                  <div className="stat-value">{relativeTime(aggReconciliation?.latest?.completedAt ?? aggReconciliation?.latest?.startedAt)}</div>
                  <div className="stat-hint">{aggReconciliation?.latest ? `${aggReconciliation.latest.status} · ${aggReconciliation.latest.sourceFactVarianceCount} variances` : "never run"}</div>
                </div>
                <div className="stat">
                  <div className="stat-label">Tracker vs submitted variance</div>
                  <div className="stat-value" style={{ color: (aggReconciliation?.varianceCount ?? 0) > 0 ? "#b91c1c" : undefined }}>
                    {aggReconciliation?.varianceCount ?? 0}
                  </div>
                  <div className="stat-hint"><Link to="/admin/reconciliation">Reconciliation & integrity</Link></div>
                </div>
              </div>
            </section>
          )}

          <section className="section">
            <div className="section-head">
              <h2>Pipeline</h2>
              <Link to="/admin/jobs" className="linkbtn">Jobs & DLQ</Link>
            </div>
            {pipelineStats.length === 0 ? (
              <div className="empty">No pipeline data yet.</div>
            ) : (
              <div className="stat-grid">
                {pipelineStats.map((s) => (
                  <div key={s.label} className="stat">
                    <div className="stat-label">{s.label}</div>
                    <div className="stat-value">{s.value}</div>
                  </div>
                ))}
                <div className="stat">
                  <div className="stat-label">Failures (24h)</div>
                  <div className="stat-value" style={{ color: failed24h > 0 ? "#b91c1c" : undefined }}>{failed24h}</div>
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
